import { useCallback } from 'react'

import type { DataTableFilterValue } from './data-table.interface'

interface UseDataTableFiltersProps {
  filterValueList: DataTableFilterValue[]
  onFilterChange?: (filterValueList: DataTableFilterValue[]) => void
}

interface UseDataTableFiltersReturn {
  hasActiveFilters: boolean
  handleFilterChange: (filterId: string, valueList: string[]) => void
  handleClearFilters: () => void
  getFilterValues: (filterId: string) => string[]
}

/**
 * Hook for managing data table filter state and handlers.
 * Provides functionality to update, clear, and retrieve filter values.
 */
export const useDataTableFilters = ({
  filterValueList,
  onFilterChange,
}: UseDataTableFiltersProps): UseDataTableFiltersReturn => {
  const hasActiveFilters = filterValueList.some((f) => f.valueList.length > 0)

  const handleFilterChange = useCallback(
    (filterId: string, valueList: string[]): void => {
      const existingFilterList = filterValueList.filter((f) => f.filterId !== filterId)
      const newFilterValueList =
        valueList.length > 0 ? [...existingFilterList, { filterId, valueList }] : existingFilterList
      onFilterChange?.(newFilterValueList)
    },
    [filterValueList, onFilterChange]
  )

  const handleClearFilters = useCallback((): void => {
    onFilterChange?.([])
  }, [onFilterChange])

  const getFilterValues = useCallback(
    (filterId: string): string[] => {
      const filter = filterValueList.find((f) => f.filterId === filterId)
      return filter?.valueList ?? []
    },
    [filterValueList]
  )

  return {
    hasActiveFilters,
    handleFilterChange,
    handleClearFilters,
    getFilterValues,
  }
}
